import type {
  ChronologySectionName,
  ChronologyDataset,
  ChronologyHistory,
} from '@/utils/types'

type DatasetStatus = 'active' | 'terminated' | 'none'

const sectionOrder: ChronologySectionName[] = [
  'loan',
  'collateral',
  'liability',
  'contract',
  'person_short',
]

const sectionTitles: Record<ChronologySectionName, string> = {
  loan: 'Активні операції',
  collateral: 'Забезпечення',
  liability: 'Фінансові зобов’язання',
  contract: 'Договори',
  person_short: 'Пов’язані особи',
}

const sectionNounForms: Record<
  ChronologySectionName,
  [string, string, string]
> = {
  loan: ['операція', 'операції', 'операцій'],
  collateral: ['актив', 'активи', 'активів'],
  liability: ['зобов’язання', 'зобов’язання', 'зобов’язань'],
  contract: ['договір', 'договори', 'договорів'],
  person_short: ['особа', 'особи', 'осіб'],
}

const submissionNounForms: [string, string, string] = [
  'подача',
  'подачі',
  'подач',
]

const pluralize = (n: number, forms: [string, string, string]): string => {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return forms[0]
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1]
  return forms[2]
}

const isTerminatedEvent = (event: string | null | undefined): boolean => {
  return !!event && event.trim().startsWith('Припинена')
}

const getEventStatus = (event: string | null | undefined): DatasetStatus => {
  if (!event || !event.trim()) return 'none'

  return isTerminatedEvent(event) ? 'terminated' : 'active'
}

const getLastHistory = (
  dataset: ChronologyDataset
): ChronologyHistory | null => {
  return dataset.history.length
    ? dataset.history[dataset.history.length - 1]
    : null
}

const getDatasetStatus = (dataset: ChronologyDataset): DatasetStatus => {
  return getEventStatus(getLastHistory(dataset)?.f150Event)
}

const statusChipColor: Record<DatasetStatus, string> = {
  active: 'success',
  terminated: 'grey-darken-1',
  none: 'warning',
}

const statusDotColor: Record<DatasetStatus, string> = {
  active: 'rgb(var(--v-theme-success))',
  terminated: '#9e9e9e',
  none: 'rgb(var(--v-theme-warning))',
}

const getDateOnly = (dateTime: string | null | undefined): string => {
  return (dateTime || '').split(' ')[0] || ''
}

const getDatasetStatusLabel = (dataset: ChronologyDataset): string => {
  const status = getDatasetStatus(dataset)
  if (status === 'none') return 'Без статусу'
  if (status === 'terminated') {
    return `Припинена ${getDateOnly(getLastHistory(dataset)?.sentAt)}`
  }

  return 'Діюча'
}

const getDatasetNote = (dataset: ChronologyDataset): string => {
  const status = getDatasetStatus(dataset)
  const last = getLastHistory(dataset)

  if (status === 'none') {
    return 'У подачах не зафіксовано зміни статусу (подія F150 відсутня).'
  }
  if (status === 'terminated' && last) {
    return `${last.f150Event}. Остання подача: ${last.sentAt}.`
  }

  return ''
}

const getTerminatedCount = (datasets: ChronologyDataset[]): number => {
  return datasets.filter(d => getDatasetStatus(d) === 'terminated').length
}

const getSectionCountLabel = (
  name: ChronologySectionName,
  datasets: ChronologyDataset[]
): string => {
  return `${datasets.length} ${pluralize(datasets.length, sectionNounForms[name])}`
}

const getSubmissionsLabel = (dataset: ChronologyDataset): string => {
  const n = dataset.history.length

  return `${n} ${pluralize(n, submissionNounForms)}`
}

const getDatasetRange = (dataset: ChronologyDataset): string => {
  const history = dataset.history
  if (!history.length) return ''
  const from = getDateOnly(history[0].sentAt)
  const to = getDateOnly(history[history.length - 1].sentAt)

  return from === to ? from : `${from} — ${to}`
}

const getEventLabel = (event: string | null | undefined): string => {
  return event && event.trim() ? event : 'Без події'
}

export {
  DatasetStatus,
  sectionOrder,
  sectionTitles,
  getEventStatus,
  getDatasetStatus,
  statusChipColor,
  statusDotColor,
  getDatasetStatusLabel,
  getDatasetNote,
  getTerminatedCount,
  getSectionCountLabel,
  getSubmissionsLabel,
  getDatasetRange,
  getEventLabel,
}
